import { AUTH_ENDPOINTS } from "./endpoints";
import { useApiClient } from "../apiClients";
import type { CoreUser, PatchMeInput } from "./types";

export const useAuthApi = () => {
    // If your identity service has a key (e.g. "identity"), use it here.
    // Otherwise use the default client.
    const api = useApiClient("identity");

    const loginBasic = async (username: string, password: string): Promise<boolean> => {
        await api.post(AUTH_ENDPOINTS.LOGIN, null, {
            auth: { username, password },
        });
        try {
            await me(); // sanity check session
            return true;
        } catch {
            return false;
        }
    };

    const logout = async (): Promise<void> => {
        try {
            await api.post(AUTH_ENDPOINTS.LOGOUT);
        } catch {
            /* no-op */
        }
    };

    const me = async <T extends CoreUser = CoreUser>(): Promise<T> => {
        const { data } = await api.get<T>(AUTH_ENDPOINTS.ME);
        return data;
    };

    const patchMe = async <T extends CoreUser = CoreUser>(input: PatchMeInput): Promise<T> => {
        const { data } = await api.patch<T>(AUTH_ENDPOINTS.ME, input);
        return data;
    };

    const updateMetadataKey = async <T extends CoreUser = CoreUser>(
        key: string,
        value: unknown
    ): Promise<T> => {
        return patchMe<T>({ metadata: { [key]: value } });
    };

    const isAuthenticated = async (): Promise<boolean> => {
        try {
            await me();
            return true;
        } catch {
            return false;
        }
    };

    return {
        loginBasic,
        logout,
        me,
        patchMe,
        updateMetadataKey,
        isAuthenticated,
    };
};
